// Payment-link delivery API helpers used by Practice > Templates > Defaults
// and the Payments & refunds tab.
//
// Consolidated onto the canonical implementations:
//   - Error text extraction -> src/utils/extractApiError.js
//   - Send/resend command   -> src/redux/slices/lead/paymentLinkThunks.js
//     (that module's own header calls it "the single source of truth other
//     implementers should import from" — this file adapts its object-arg,
//     {ok, data, error, status} calling convention to the positional-arg
//     signature the settings/Payments-tab components here were built against,
//     rather than keeping a second parallel implementation.)
//
// Endpoints implement the fixed contract in
// /Users/admin/Desktop/2026-09-23-payment-link-delivery-waves.md (sections A/B/C/D).
import { callApi } from 'src/utils/apiCall';
import extractApiErrorMessage from 'src/utils/extractApiError';
import {
    sendPaymentLink as sendPaymentLinkCommand,
    resendPaymentLink as resendPaymentLinkCommand,
} from 'src/redux/slices/lead/paymentLinkThunks';

export { extractApiErrorMessage as extractPaymentLinkErrorText };

/**
 * Per-channel block/failure reason out of a prerequisites_failed (400) or
 * partial-failure (201) response — used to render channel-specific guidance.
 * Excludes `not_sent_other_channel_blocked` — that code means this channel's
 * own config was fine and it was simply skipped because the other requested
 * channel failed prerequisites, not a problem with this channel's recipient.
 */
export function getChannelError(responseData, channel) {
    const info = responseData?.channels?.[channel];
    if (!info || info.code === 'not_sent_other_channel_blocked') return null;
    return info.error || null;
}

const OTHER_CHANNEL_BLOCKED_CODE = 'not_sent_other_channel_blocked';

/**
 * Human-readable label + "is this actually a problem" flag for one channel's
 * entry in a `channels` result map. Distinguishes a real failure/block from a
 * channel that was simply skipped because a sibling channel blocked the send.
 */
export function describeChannelState(info) {
    if (!info) return { label: 'Not requested', isOtherChannelBlock: false };
    if (info.code === OTHER_CHANNEL_BLOCKED_CODE) {
        return { label: 'Not sent (other channel blocked)', isOtherChannelBlock: true };
    }
    return { label: info.state, isOtherChannelBlock: false };
}

// Contract A — send/recover a payment link. Delegates to paymentLinkThunks.js;
// returns { ok, data, error, status } (error is already the extracted message).
export function sendPaymentLink(practiceId, leadId, treatmentId, payload) {
    return sendPaymentLinkCommand({ practiceId, leadId, treatmentId, body: payload });
}

// Contract B — resend using the existing open checkout. Same delegation as above.
export function resendPaymentLink(practiceId, leadId, treatmentId, paymentId, payload) {
    return resendPaymentLinkCommand({ practiceId, leadId, treatmentId, paymentId, body: payload });
}

// Contract C — eligible WhatsApp templates for the payment-link default-template
// setting. No equivalent in paymentLinkThunks.js (that module only covers the
// send/resend command), so this stays a direct callApi call.
// -> { connected: bool, results: [{ id, name, language, status, compatible, reason, preview_body }] }
export function getEligibleWhatsappTemplates(practiceId) {
    return callApi(`/api/v1/practices/${practiceId}/payment-link-whatsapp-templates/`);
}

const paymentBaseUrl = (practiceId, leadId, treatmentId, paymentId) =>
    `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}`;

// Contract D — mark a payment paid outside Stripe (phone/in-practice/bank
// transfer/other/email), or revert a payment a patient paid over the phone
// instead of via the still-open Stripe link. No equivalent command exists in
// paymentLinkThunks.js, so these are direct callApi calls.
// payload -> { paid_at?: 'YYYY-MM-DD' | ISO, mode_of_payment?: string, note?: string }
// 200 -> payment JSON (may include warnings: ['stripe_link_not_expired']).
// 409 -> { detail, code: 'already_paid' }. 400 -> invalid date/mode.
export function markPaymentPaid(practiceId, leadId, treatmentId, paymentId, payload) {
    return callApi(`${paymentBaseUrl(practiceId, leadId, treatmentId, paymentId)}/mark-paid/`, 'post', payload);
}

// 200 -> payment JSON. 409 -> { detail, code: 'stripe_paid' } for a payment
// that was actually paid through Stripe (mark-unpaid only applies to
// paid_manually payments).
export function markPaymentUnpaid(practiceId, leadId, treatmentId, paymentId) {
    return callApi(`${paymentBaseUrl(practiceId, leadId, treatmentId, paymentId)}/mark-unpaid/`, 'post');
}
